<template>
  <!-- web url or url_postback -->
  <a
    v-if="isExternalLink"
    class="btn"
    :class="`${variant}-variant`"
    :href="getHref()"
    target="_blank"
    rel="nofollow"
    :title="button.text"
    @click="handleClick"
  >
    <IconExternal v-if="isExternalLink" width="16" height="16" aria-hidden="true" />
    <span aria-hidden="true">
      {{ button.text }}
    </span>
  </a>
  <!-- postback then we use a button, because we don't have an url -->
  <button v-if="isPostback" class="btn" :class="`${variant}-variant`" @click="handleClick">
    <span>
      {{ button.text }}
    </span>
  </button>
</template>

<script setup lang="ts">
import logger from "@/logger";
import { computed } from "vue";
import signals from "../../../common/signals";
import type { Intent } from "@/types";
import IconExternal from "@/svg/IconExternal.vue";
import type {
  Button,
  PostbackButton,
  UrlPostbackButton,
  WebUrlButton,
} from "@/services/bot/messageBuilder";

const emit = defineEmits<{
  onIntent: [intent: Intent];
}>();

const props = defineProps<{
  button: Button;

  source: string;
  variant?: string;
}>();

// console.log("BUTTON", props.button.type, props.button);

// Type guard functions
function isWebUrlButton(button: Button): button is WebUrlButton {
  return button.type === "web_url";
}

function isPostbackButton(button: Button): button is PostbackButton {
  return button.type === "postback";
}

function isUrlPostbackButton(button: Button): button is UrlPostbackButton {
  return button.type === "url_postback";
}

const handleClick = () => {
  // we signal click on all button types
  signals.click(props.source, {
    source: "buttonList",
    www: "wwww",
    text: props.button.text,
    target: isPostbackButton(props.button) ? "postback" : props.button?.url,
    type: props.button.type,
  });
  // if we have a web_url button, we don't emit an intent
  if (isWebUrlButton(props.button)) {
    return;
  }
  // if we have no postback payload, on postback or url_postback we warn
  // this should not happen, but we handle it gracefully
  if (
    (isPostbackButton(props.button) || isUrlPostbackButton(props.button)) &&
    !props.button.intent
  ) {
    logger.warn("no intent found for button click", props.button);
    return;
  }
  // if we have an intent on postback or url_postback, we emit it
  if (props.button.intent) {
    logger.log(`${props.button.type} button clicked`, props.button.intent);
    emit("onIntent", props.button.intent);
  }
};

const isPostback = computed(() => isPostbackButton(props.button));
const isExternalLink = computed(
  () => isUrlPostbackButton(props.button) || isWebUrlButton(props.button)
);

const getHref = () => {
  if (isWebUrlButton(props.button) || isUrlPostbackButton(props.button)) {
    // we only have urls on web_url and url_postback buttons
    // and if we have an url we can have different urls types
    // mailto, tel, web_url
    return props.button.url;
  }
};
</script>

<style lang="scss" scoped>
/* @include kw_btn; */
.btn {
  @include default-font;
  @include default-focus;

  position: relative;
  text-decoration: none;
  appearance: none;
  background: $kw-global-background-white;
  color: $kw-global-textcolor;
  box-shadow: none;
  border-radius: var(--kw-widget-global-borderradius-inner);
  border: 1px solid var(--moin-widget-button-bordercolor);

  height: 32px;
  max-width: 100%;
  width: fit-content;
  display: flex;
  align-items: center;
  transition: border-color 0.2s ease-in-out;

  margin: 0;
  padding: 0 8px;
  gap: 0.5rem;

  box-sizing: content-box;

  text-transform: none;
  font-weight: 400;
  font-size: 1.5rem;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  > span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    border-radius: var(--kw-widget-global-borderradius-inner);
  }

  .btn_link {
    position: absolute;
    top: 10px;
    right: 6px;
    height: 10px;
    margin-left: 4px;
  }

  &:hover {
    border-color: $kw-default-button-border-color-hover;
    transition: border-color 0.2s ease-in-out;
  }

  &.buttonList-variant {
    background-color: var(--moin-message-buttonlist-background, white);
    color: var(--moin-message-buttonlist-textcolor, #333);
    border-color: var(--moin-message-buttonlist-bordercolor, $kw-default-button-border-color);

    &:hover {
      border-color: var(
        --moin-message-buttonlist-textcolor-hover,
        $kw-default-button-border-color-hover
      );
    }
  }

  &.card-variant {
    // not sure why we reduced the font size here
    // some weird inconsistency with the other buttons
    font-size: 1.5rem;
    /* margin-bottom: 7px;
    &:last-child {
      margin-bottom: 0px;
    } */

    background-color: var(
      --moin-message-cardbutton-background,
      $kw-messagesTypes-card-button-background
    );
    border: 1px solid
      var(--moin-message-cardbutton-bordercolor, $kw-messagesTypes-card-button-border);

    color: var(--moin-message-cardbutton-textcolor, $kw-messagesTypes-card-button-textcolor);

    width: 100%;

    &:hover {
      cursor: pointer;

      border: 1px solid
        var(--moin-message-cardbutton-bordercolor-hover, $kw-default-button-border-color-hover);
      @include default-focus;
    }
  }

  > svg {
    flex-shrink: 0;
  }
}
</style>
